import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { site } from '@/mocks/site';
import { categories } from '@/mocks/categories';

type SiteHeaderProps = {
  overlay?: boolean;
};

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setArchiveOpen(false);
  }, [location.pathname]);

  const solid = !overlay || scrolled || menuOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/80'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 md:h-20 items-center justify-between px-4 md:px-8">
        <Link to="/" className="flex items-center gap-3 shrink-0" aria-label="反差现场 首页">
          <span className="grid grid-cols-2 w-8 h-8 md:w-9 md:h-9 rounded-md overflow-hidden shrink-0">
            <span className="bg-primary-500" />
            <span className="bg-secondary-500" />
            <span className="bg-accent-500" />
            <span className="bg-primary-700" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg md:text-xl font-black tracking-wide ${
                solid ? 'text-foreground-950' : 'text-background-50'
              }`}
            >
              {site.name}
            </span>
            <span
              className={`hidden sm:block text-[10px] md:text-[11px] tracking-[0.3em] mt-1 font-label ${
                solid ? 'text-foreground-500' : 'text-background-200'
              }`}
            >
              {site.slogan}
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
          <Link
            to="/"
            className={`px-4 py-2 rounded-md text-sm font-label transition-colors ${
              solid
                ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                : 'text-background-100 hover:text-background-50 hover:bg-white/10'
            }`}
          >
            首页
          </Link>
          <div
            className="relative"
            onMouseEnter={() => setArchiveOpen(true)}
            onMouseLeave={() => setArchiveOpen(false)}
          >
            <Link
              to="/category/live"
              className={`flex items-center gap-1 px-4 py-2 rounded-md text-sm font-label transition-colors ${
                solid
                  ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                  : 'text-background-100 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              反差档案
              <i className="ri-arrow-down-s-line text-base leading-none" aria-hidden="true" />
            </Link>
            {archiveOpen && (
              <div className="absolute left-0 top-full pt-2 w-64">
                <div className="bg-background-50 border border-background-200 rounded-lg p-2 shadow-none">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-start gap-3 px-3 py-2.5 rounded-md hover:bg-background-100 transition-colors"
                    >
                      <span className="w-8 h-8 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                        <i className={`${cat.icon} text-base leading-none`} aria-hidden="true" />
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm font-medium text-foreground-900">
                          {cat.name}
                        </span>
                        <span className="text-xs text-foreground-500 mt-0.5 line-clamp-1">
                          {cat.desc.slice(0, 18)}…
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <Link
            to="/about"
            className={`px-4 py-2 rounded-md text-sm font-label transition-colors ${
              solid
                ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                : 'text-background-100 hover:text-background-50 hover:bg-white/10'
            }`}
          >
            关于我们
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/about#contact"
            className="hidden md:inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label px-4 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-send-plane-line text-base leading-none" aria-hidden="true" />
            我要投稿
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? '关闭菜单' : '展开菜单'}
            aria-expanded={menuOpen}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
              solid ? 'text-foreground-900 hover:bg-background-100' : 'text-background-50 hover:bg-white/10'
            }`}
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl leading-none`} aria-hidden="true" />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            <Link to="/" className="px-3 py-3 rounded-md text-sm text-foreground-800 hover:bg-background-100">
              首页
            </Link>
            <span className="px-3 pt-4 pb-1 text-xs tracking-widest text-foreground-400 font-label">
              反差档案
            </span>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-3 px-3 py-3 rounded-md text-sm text-foreground-800 hover:bg-background-100"
              >
                <span className="w-7 h-7 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm leading-none`} aria-hidden="true" />
                </span>
                {cat.name}
              </Link>
            ))}
            <Link to="/about" className="px-3 py-3 rounded-md text-sm text-foreground-800 hover:bg-background-100">
              关于我们
            </Link>
            <Link
              to="/about#contact"
              className="mt-2 flex items-center justify-center gap-2 bg-primary-500 text-background-50 text-sm px-4 py-3 rounded-md"
            >
              <i className="ri-send-plane-line text-base leading-none" aria-hidden="true" />
              我要投稿
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}